import { getFunctions, httpsCallable } from "firebase/functions";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase/config";

class OTPAuthService {
  constructor() {
    this.functions = getFunctions();
  }

  /**
   * Send OTP to email
   * @param {string} email - User's email address
   * @param {string} purpose - Purpose of OTP (login, signup, reset)
   * @returns {Promise<Object>} Response with success status and expiry time
   */
  async sendOTP(email, purpose = "login") {
    try {
      const generateOTP = httpsCallable(this.functions, "generateOTP");
      const result = await generateOTP({ email, purpose });

      return {
        success: result.data.success,
        message: result.data.message,
        expiresIn: result.data.expiresIn,
      };
    } catch (error) {
      console.error("Error sending OTP:", error);

      // Handle specific error codes
      if (error.code === "functions/invalid-argument") {
        throw new Error("Invalid email address");
      } else if (error.code === "functions/internal") {
        throw new Error("Failed to send OTP. Please try again later.");
      }

      throw error;
    }
  }

  /**
   * Verify OTP and authenticate user
   * @param {string} email - User's email address
   * @param {string} otp - OTP code entered by user
   * @param {string} purpose - Purpose of OTP (should match what was sent)
   * @returns {Promise<Object>} Authentication result with user info
   */
  async verifyOTP(email, otp, purpose = "login") {
    try {
      const verifyOTPFunction = httpsCallable(this.functions, "verifyOTP");
      const result = await verifyOTPFunction({ email, otp, purpose });

      if (result.data.success && result.data.tempPassword) {
        // Sign in with temporary password
        const userCredential = await signInWithEmailAndPassword(
          auth,
          email,
          result.data.tempPassword
        );

        return {
          success: true,
          user: userCredential.user,
          isNewUser: result.data.isNewUser,
        };
      }

      throw new Error("OTP verification failed");
    } catch (error) {
      console.error("Error verifying OTP:", error);

      // Handle specific error codes
      if (error.code === "functions/invalid-argument") {
        throw new Error("Invalid OTP code");
      } else if (error.code === "functions/not-found") {
        throw new Error("OTP not found or expired. Please request a new one.");
      } else if (error.code === "functions/permission-denied") {
        if (error.message.includes("already been used")) {
          throw new Error("This OTP has already been used");
        } else if (error.message.includes("Too many failed attempts")) {
          throw new Error("Too many failed attempts. Please request a new OTP.");
        }
      } else if (error.code === "functions/deadline-exceeded") {
        throw new Error("OTP has expired. Please request a new one.");
      }

      throw error;
    }
  }

  /**
   * Resend OTP to email
   * @param {string} email - User's email address
   * @param {string} purpose - Purpose of OTP
   * @returns {Promise<Object>} Response with success status
   */
  async resendOTP(email, purpose = "login") {
    return this.sendOTP(email, purpose);
  }

  /**
   * Check if OTP authentication is available
   * @returns {boolean} True if OTP auth is configured
   */
  isOTPAuthAvailable() {
    try {
      // Check if functions are available
      return !!this.functions;
    } catch (error) {
      console.error("OTP auth not available:", error);
      return false;
    }
  }

  /**
   * Format OTP for display (add spacing for readability)
   * @param {string} otp - Raw OTP string
   * @returns {string} Formatted OTP
   */
  formatOTP(otp) {
    if (!otp || typeof otp !== "string") return "";

    // Add space after every 3 digits for 6-digit OTP
    if (otp.length === 6) {
      return `${otp.slice(0, 3)} ${otp.slice(3)}`;
    }

    return otp;
  }

  /**
   * Validate OTP format
   * @param {string} otp - OTP to validate
   * @returns {boolean} True if valid format
   */
  validateOTPFormat(otp) {
    if (!otp || typeof otp !== "string") return false;

    // Remove spaces and check if it's 6 digits
    const cleanOTP = otp.replace(/\s/g, "");
    return /^\d{6}$/.test(cleanOTP);
  }

  /**
   * Clean OTP input (remove spaces and non-digits)
   * @param {string} otp - Raw OTP input
   * @returns {string} Cleaned OTP
   */
  cleanOTPInput(otp) {
    if (!otp || typeof otp !== "string") return "";
    return otp.replace(/\D/g, "");
  }

  /**
   * Send OTP for mentor registration
   * @param {string} email - Mentor's email address
   * @param {string} inviteCode - Invitation code
   * @param {Object} mentorData - Mentor registration data
   * @returns {Promise<Object>} Response with success status
   */
  async sendMentorOTP(email, inviteCode, mentorData) {
    try {
      const generateMentorOTP = httpsCallable(this.functions, "generateMentorOTP");
      const result = await generateMentorOTP({ email, inviteCode, mentorData });

      return {
        success: result.data.success,
        message: result.data.message,
        expiresIn: result.data.expiresIn,
      };
    } catch (error) {
      console.error("Error sending mentor OTP:", error);

      if (error.code === "functions/invalid-argument") {
        throw new Error("招待コードまたはメールアドレスが無効です");
      } else if (error.code === "functions/not-found") {
        throw new Error("有効な招待が見つかりません");
      } else if (error.code === "functions/deadline-exceeded") {
        throw new Error("招待の有効期限が切れています");
      } else if (error.code === "functions/internal") {
        throw new Error("OTP送信に失敗しました。しばらくしてから再試行してください。");
      }

      throw error;
    }
  }

  /**
   * Send invitation email for mentor registration
   * @param {string} email - Mentor's email address
   * @param {string} inviteCode - Invitation code
   * @param {string} invitationUrl - Registration URL with invite code
   * @returns {Promise<Object>} Response with success status
   */
  async sendMentorInvitationEmail(email, inviteCode, invitationUrl) {
    try {
      const sendInvitationEmail = httpsCallable(this.functions, "sendMentorInvitationEmail");
      const result = await sendInvitationEmail({ email, inviteCode, registrationUrl: invitationUrl });

      return {
        success: result.data.success,
        message: result.data.message,
      };
    } catch (error) {
      console.error("Error sending mentor invitation email:", error);

      if (error.code === "functions/invalid-argument") {
        throw new Error("招待情報が不正です");
      } else if (error.code === "functions/internal") {
        throw new Error("招待メール送信に失敗しました。しばらくしてから再試行してください。");
      }

      throw error;
    }
  }

  /**
   * Verify OTP for mentor registration
   * @param {string} email - Mentor's email address
   * @param {string} otp - OTP code entered by mentor
   * @returns {Promise<Object>} Registration result with mentor info
   */
  async verifyMentorOTP(email, otp) {
    try {
      const verifyMentorOTPFunction = httpsCallable(this.functions, "verifyMentorOTP");
      const result = await verifyMentorOTPFunction({ email, otp });

      if (result.data.success && result.data.tempPassword) {
        // Sign in with temporary password
        const userCredential = await signInWithEmailAndPassword(
          auth,
          email,
          result.data.tempPassword
        );

        return {
          success: true,
          user: userCredential.user,
          mentorId: result.data.mentorId,
          uid: result.data.uid,
          message: result.data.message,
        };
      }

      throw new Error("メンターOTP検証に失敗しました");
    } catch (error) {
      console.error("Error verifying mentor OTP:", error);

      if (error.code === "functions/invalid-argument") {
        throw new Error("無効な認証コードです");
      } else if (error.code === "functions/not-found") {
        if (error.message.includes("Mentor registration data")) {
          throw new Error("登録データが見つかりません。最初からやり直してください。");
        }
        throw new Error("認証コードが見つからないか期限切れです。新しいコードをリクエストしてください。");
      } else if (error.code === "functions/permission-denied") {
        if (error.message.includes("already been used")) {
          throw new Error("この認証コードは既に使用されています");
        } else if (error.message.includes("Too many failed attempts")) {
          throw new Error("試行回数が多すぎます。新しい認証コードをリクエストしてください。");
        } else if (error.message.includes("Invalid OTP for mentor")) {
          throw new Error("メンター登録用の認証コードではありません");
        }
      } else if (error.code === "functions/deadline-exceeded") {
        throw new Error("認証コードの有効期限が切れています。新しいコードをリクエストしてください。");
      } else if (error.code === "functions/already-exists") {
        throw new Error("このメールアドレスは既に登録されています。ログインしてください。");
      }

      throw error;
    }
  }
}

// Export singleton instance
const otpAuthService = new OTPAuthService();
export default otpAuthService;